import { addOrUpdateCompany, getCompaniesList } from "actions/Catalogs";
import Grid from "components/Grid";
import InputCustom from "components/InputCustom";
import Modal from "components/Modal";
import Joi from "joi";
import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import useValidator from "react-joi";
import { useDispatch, useSelector } from "react-redux";
import { notify } from "utils";

const Companies = () => {
  const [t] = useTranslation("catalog");
  const [tg] = useTranslation("global");
  const dispatch = useDispatch();
  const [openModal, setOpenModal] = React.useState(false);
  const buttonRef = useRef();
  const { currentProject } = useSelector((state) => state.auth);
  const { companiesList } = useSelector((state) => state.catalog);
  const [showError, setShowError] = React.useState(false);
  const [id, setId] = React.useState();
  const [isLoad, setIsLoad] = React.useState(false);
  const { state, setData, setExplicitField, validate } = useValidator({
    initialData: {
      name: null,
      serviceDescription: null,
      dayCost: null,
      iva: null,
      status: true,
    },
    schema: Joi.object({
      name: Joi.string()
        .min(5)
        .max(100)
        .required()
        .messages({
          "string.empty": t("error.empty"),
          "string.base": t("error.type", { type: "string" }),
          "string.min": t("error.min_length", { quantity: "5" }),
          "string.max": t("error.max", { quantity: "100" }),
          "any.required": t("error.required"),
        }),
      serviceDescription: Joi.string()
        .min(5)
        .max(250)
        .required()
        .messages({
          "string.empty": t("error.empty"),
          "string.base": t("error.type", { type: "string" }),
          "string.min": t("error.min_length", { quantity: "5" }),
          "string.max": t("error.max", { quantity: "250" }),
          "any.required": t("error.required"),
        }),
      dayCost: Joi.number()
        .custom((value, helpers) => {
          const s = String(value);

          // Validar que sea un número válido con formato decimal
          if (!/^\d+(\.\d+)?$/.test(s)) {
            return helpers.error("number.base");
          }

          // Separar parte entera y decimal
          const [integerPart, decimalPart] = s.split(".");

          // Validar mínimo de enteros
          if (integerPart.length < 2) {
            return helpers.error("number.min");
          }

          // Validar máximo de enteros
          if (integerPart.length > 4) {
            return helpers.error("number.max");
          }

          // Validar máximo de decimales
          if (decimalPart && decimalPart.length > 2) {
            return helpers.error("number.decimals");
          }

          return value;
        })
        .required()
        .messages({
          "number.min":
            t("error.min", { quantity: "2" }) || "Mínimo 2 dígitos enteros",
          "number.max":
            t("error.max", { quantity: "4" }) || "Máximo 4 dígitos enteros",
          "number.decimals": "Máximo 2 decimales",
          "number.base": t("error.type", { type: "decimal" }),
          "any.required": t("error.required"),
        }),
      iva: Joi.number()
        .custom((value, helpers) => {
          const s = String(value);

          // Validar que sea un número válido con formato decimal
          if (!/^\d+(\.\d+)?$/.test(s)) {
            return helpers.error("number.base");
          }

          // Separar parte entera y decimal
          const [integerPart, decimalPart] = s.split(".");

          // Validar mínimo de enteros
          if (integerPart.length < 2) {
            return helpers.error("number.min");
          }

          // Validar máximo de enteros
          if (integerPart.length > 4) {
            return helpers.error("number.max");
          }

          // Validar máximo de decimales
          if (decimalPart && decimalPart.length > 2) {
            return helpers.error("number.decimals");
          }

          return value;
        })
        .required()
        .messages({
          "number.min":
            t("error.min", { quantity: "2" }) || "Mínimo 2 dígitos enteros",
          "number.max":
            t("error.max", { quantity: "4" }) || "Máximo 4 dígitos enteros",
          "number.decimals": "Máximo 2 decimales",
          "number.base": t("error.type", { type: "decimal" }),
          "any.required": t("error.required"),
        }),
      status: Joi.boolean()
        .required()
        .messages({
          "boolean.base": t("error.type", { type: "boolean" }),
          "any.required": t("error.required"),
        }),
    }),
    explicitCheck: {
      name: false,
      description: false,
      costPerDay: false,
      iva: false,
    },
    validationOptions: {
      abortEarly: true,
    },
  });

  useEffect(() => {
    if (currentProject && currentProject?.id) {
      dispatch(getCompaniesList());
    }
  }, [dispatch, currentProject]);

  const handleOnSubmit = async () => {
    if (state.$all_errors.length > 0 || state.$all_source_errors.length > 0) {
      setShowError(true);
      validate();
      notify("error", t("error.general_toast"), 1200);
      return;
    }

    setIsLoad(true);
    if (buttonRef & buttonRef?.current) buttonRef.current.disabled = true;
    let isSuccess = await dispatch(
      addOrUpdateCompany({
        ...state.$data,
        id,
      })
    );
    if (buttonRef & buttonRef?.current) buttonRef.current.disabled = false;
    setIsLoad(false);
    setShowError(false);
    if (isSuccess) {
      dispatch(getCompaniesList());
      setData((old) => ({
        ...old,
        name: null,
        serviceDescription: null,
        dayCost: null,
        iva: null,
        status: true,
      }));
      setId(null);
      setOpenModal(false);
    }
  };

  return (
    <>
      <Grid
        title={t("companies.title")}
        columns={[
          {
            Header: "Id",
            accessor: "id",
            filter: "text",
            width: "10%",
          },
          {
            Header: t("companies.name"),
            accessor: "name",
            filter: "text",
            width: "18%",
          },
          {
            Header: t("companies.serviceDescription"),
            accessor: "serviceDescription",
            filter: "text",
            width: "18%",
          },
          {
            Header: t("companies.dayCost"),
            accessor: "dayCost",
            filter: "text",
            width: "18%",
          },
          {
            Header: t("companies.iva"),
            accessor: "iva",
            filter: "text",
            width: "18%",
          },
          {
            Header: t("companies.status"),
            accessor: "status",
            filter: "text",
            width: "18%",
            Cell: ({ value }) => {
              return value ? tg("select.active") : tg("select.inactive");
            },
          },
        ]}
        data={companiesList || []}
        handleAdd={() => {
          setData((old) => ({
            ...old,
            name: null,
            serviceDescription: null,
            dayCost: null,
            iva: null,
            status: true,
          }));
          setOpenModal(true);
        }}
        handleEdit={(row) => {
          const { name, serviceDescription, dayCost, iva, id, status } = row;
          setId(id);
          setData((old) => ({
            ...old,
            name,
            serviceDescription,
            dayCost,
            iva,
            status,
          }));
          setOpenModal(true);
        }}
        write={true}
      />
      {openModal ? (
        <Modal
          openModal={openModal}
          closeModal={() => {
            setData((old) => ({
              ...old,
              name: null,
              description: null,
            }));
            setShowError(false);
            setOpenModal(false);
            setId(null);
          }}
          titleModal="Empresas"
          onSubmit={handleOnSubmit}
          disableAdd={state.$invalid}
          iconEdit={id > 0 ? true : false}
          buttonRef={buttonRef}
          processFetch={isLoad}
        >
          <h4 className="font-[Gravity] font-bold tex-[18px] my-2">
            {t("companies.info_modal")}
          </h4>
          <div className="flex flex-wrap ">
            {id && (
              <InputCustom
                type={"number"}
                labelText={"Id Empresa:"}
                value={id}
                disabled={true}
              />
            )}
            <InputCustom
              type={"text"}
              labelText={t("companies.name")}
              errorMessage={
                showError && state.$errors.name.length > 0
                  ? state.$errors.name[0].$message
                  : null
              }
              onBlur={() => setExplicitField("name", true)}
              onChange={(e) => {
                e.persist();
                setData((old) => ({
                  ...old,
                  name: e.target.value,
                }));
              }}
              value={state.$data.name}
            />
            <InputCustom
              type={"text"}
              labelText={t("companies.serviceDescription")}
              errorMessage={
                showError && state.$errors.serviceDescription.length > 0
                  ? state.$errors.serviceDescription[0].$message
                  : null
              }
              onBlur={() => setExplicitField("serviceDescription", true)}
              onChange={(e) => {
                e.persist();
                setData((old) => ({
                  ...old,
                  serviceDescription: e.target.value,
                }));
              }}
              value={state.$data.serviceDescription}
            />
            <InputCustom
              type={"number"}
              labelText={t("companies.dayCost")}
              errorMessage={
                showError && state.$errors.dayCost.length > 0
                  ? state.$errors.dayCost[0].$message
                  : null
              }
              onBlur={() => setExplicitField("dayCost", true)}
              onChange={(e) => {
                e.persist();
                setData((old) => ({
                  ...old,
                  dayCost: e.target.value,
                }));
              }}
              value={state.$data.dayCost}
            />
            <InputCustom
              type={"number"}
              labelText={t("companies.iva")}
              errorMessage={
                showError && state.$errors.iva.length > 0
                  ? state.$errors.iva[0].$message
                  : null
              }
              onBlur={() => setExplicitField("iva", true)}
              onChange={(e) => {
                e.persist();
                setData((old) => ({
                  ...old,
                  iva: e.target.value,
                }));
              }}
              value={state.$data.iva}
            />
            {id && (
              <InputCustom
                type={"select"}
                labelText={t("companies.status")}
                onChange={(e) => {
                  e.persist();
                  setData((old) => ({
                    ...old,
                    status: e.target.value === "true",
                  }));
                }}
                options={[
                  { text: "Activo", value: true },
                  { text: "Inactivo", value: false },
                ]}
                value={state.$data.status}
                onBlur={() => setExplicitField("status", true)}
                keyId="status"
              />
            )}
          </div>
        </Modal>
      ) : null}
    </>
  );
};

export default Companies;
